import './pageWrapper.less';

import * as React from 'react';

import { Col, Divider, Row } from 'antd';
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { NonIdealAction, PageWrapperProps } from './pageWrapperProps';

import { AppButton } from '@/components/appButton';
import classNames from 'classnames';
import { logout } from '@modules/securityModule';
import { useAppDispatch } from '@reduxHelpers';
import { useAppRoutes } from '@/hooks';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

export const PageWrapper: React.FC<PageWrapperProps> = (props) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();
    const appRoutes = useAppRoutes();
    const location = useLocation();
    const navigate = useNavigate();

    const topRef = React.createRef<HTMLDivElement>();
    const contentRef = React.createRef<HTMLDivElement>();

    React.useEffect(() => {
        const scrollOptions: ScrollIntoViewOptions = {
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
        };

        switch (props.scrollBehaviorOnMount) {
            case 'top': {
                topRef.current?.scrollIntoView(scrollOptions);
                break;
            }
            case 'none': {
                break;
            }
            case 'content':
            case undefined:
            default: {
                contentRef.current?.scrollIntoView(scrollOptions);
                break;
            }
        }
    }, []);

    const className = classNames('page-wrapper', props.containerClassName);

    const containerStyle: React.CSSProperties = props.isNonIdeal
        ? { ...props.style, overflow: 'hidden' }
        : { ...props.style };

    const overrideActionText = (action?: NonIdealAction): string => {
        switch (action) {
            case 'back': {
                return t('Components.PageWrapper.go_back_text');
            }
            case 'home': {
                return t('Components.PageWrapper.go_home_text');
            }
            case 'login': {
                return t('Components.PageWrapper.login_text');
            }
            case 'logout': {
                return t('Components.PageWrapper.logout_text');
            }
            case 'reload': {
                return t('Components.PageWrapper.reload_text');
            }
            default: {
                return action || '';
            }
        }
    };

    const handleActionClicked = (action: NonIdealAction): void => {
        switch (action) {
            case 'back': {
                navigate(-1);
                break;
            }

            case 'reload': {
                window.location.reload();
                break;
            }

            case 'home': {
                navigate(appRoutes.home.path);
                break;
            }

            case 'logout': {
                dispatch(logout());
                break;
            }

            case 'login': {
                navigate(appRoutes.login.path, { state: { from: location } });
                break;
            }

            default: {
                // other
                break;
            }
        }
    };

    return (
        <Row
            className={className}
            style={containerStyle}
            align="top"
            justify="center"
            ref={topRef}
        >
            <Col
                xxl={14}
                xl={16}
                lg={18}
                md={20}
                sm={24}
                xs={24}
                {...props.customTitleBreakpoints}
                style={{ padding: 5 }}
            >
                <Row
                    align="top"
                    justify="center"
                    className="page"
                >
                    {!!props.pageTitle &&
                        <Col span={24}>
                            {(typeof props.pageTitle === 'string')
                                ?
                                <Divider
                                    className="page-title"
                                >
                                    {props.pageTitle}
                                </Divider>
                                :
                                props.pageTitle
                            }
                        </Col>
                    }

                </Row>
            </Col>

            <Col
                xxl={14}
                xl={16}
                lg={18}
                md={20}
                sm={24}
                xs={24}
                {...props.customContentBreakpoints}
                style={{ padding: 5 }}
            >
                <Row
                    align="top"
                    justify="center"
                >
                    <Col span={24} className="page-wrapper-content" ref={contentRef}>
                        {props.children}
                    </Col>
                </Row>

            </Col>

            {props.isNonIdeal === true &&
                <div className="non-ideal-page">
                    <Row
                        align="middle"
                        justify="center"
                        style={{ overflowY: 'auto', overflowX: 'hidden' }}
                    >
                        <Col
                            span={24}
                            style={{ textAlign: 'center' }}
                        >
                            {!!props.nonIdealIconType
                                ?
                                <span className="non-ideal-icon">
                                    {props.nonIdealIconType}
                                </span>
                                :
                                <WarningOutlined className="non-ideal-icon" />
                            }
                        </Col>

                        <Col
                            span={24}
                            className="non-ideal-header"
                            style={{ textAlign: 'center' }}
                        >
                            {props.nonIdealHeader}
                        </Col>

                        <Col
                            span={24}
                            className="non-ideal-subheader"
                            style={{ textAlign: 'center', padding: '0 10px' }}
                        >
                            {props.nonIdealSubheader}
                        </Col>

                        <Row
                            align="middle"
                            justify="center"
                        >
                            <Col
                                xxl={24}
                                xl={24}
                                lg={24}
                                md={24}
                                sm={24}
                                xs={24}
                            >
                                <Row
                                    align="middle"
                                    justify="center"
                                    gutter={50}
                                >
                                    {!!props.nonIdealActions &&
                                        props.nonIdealActions.map((action) => {
                                            if (typeof action === 'string') {
                                                return (
                                                    <Col
                                                        key={action}
                                                    >
                                                        <AppButton
                                                            style={{ minWidth: '350px' }}
                                                            key={action}

                                                            onClick={() => handleActionClicked(action)}
                                                        >
                                                            {overrideActionText(action)}
                                                        </AppButton>
                                                    </Col>

                                                );
                                            }

                                            return (
                                                <Col
                                                    key={action.text}
                                                >
                                                    <AppButton
                                                        style={{ minWidth: '350px' }}
                                                        key={action.text}
                                                        onClick={() => action.action()}
                                                    >
                                                        {action.text}
                                                    </AppButton>
                                                </Col>
                                            );
                                        })
                                    }
                                </Row>
                            </Col>

                        </Row>

                    </Row>
                </div>

            }

            {!!props.isLoading === true &&
                <div className="page-wrapper-loading">
                    <p>
                        <LoadingOutlined className="page-wrapper-loading-icon" />
                    </p>
                    <p className="page-wrapper-loading-header">
                        {props.loadingMessage || 'Loading...'}
                    </p>
                </div>
            }
        </Row>
    );
};